import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, Link, Typography } from '@mui/material';
import { GoogleMap, LoadScriptNext, Marker } from '@react-google-maps/api';
import validator from 'validator';

import { GOOGLE_MAP_KEY } from '~/constants/common';
import { COLORS } from '~/styles/colors';
import { MicrositeLocation } from '~/types/common';

type VCardPreviewLocationProps = {
  addressString: string;
  location: MicrositeLocation;
  coordinates: null | { lat: number; lng: number };
  isMarginHorizontal?: boolean;
  isBorder?: boolean;
  boolean?: boolean;
  rounded?: boolean;
  isAnonymous?: boolean;
};

const VCardPreviewLocation = forwardRef<HTMLDivElement, VCardPreviewLocationProps>(
  (
    {
      addressString,
      coordinates,
      location,
      isMarginHorizontal = false,
      isBorder = false,
      rounded = true,
      isAnonymous = false,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    const googleMapsUrlFull = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressString)}`;
    const googleMapsCoord = `https://www.google.com/maps?q=${location.latitude},${location.longitude}&z=15&hl=en`;

    const getLocationLink = (type) => {
      switch (type) {
        case 'FULL':
          return googleMapsUrlFull;
        case 'URL':
          return location.url;
        case 'COORDINATES':
          return googleMapsCoord;
        default:
          return '#';
      }
    };

    const mapContainerStyle = {
      width: '100%',
      height: 150,
    };

    const isValid =
      (location.type === 'COORDINATES' && location.latitude && location.longitude) ||
      (location.type === 'URL' && location.url && validator.isURL(location.url)) ||
      addressString;

    if (!isValid) {
      return null;
    }

    let margin = '8px 0';

    if (isMarginHorizontal) {
      margin = '8px 2px';
    }

    if (isAnonymous) {
      margin = '8px 5px';
    }

    return (
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: '8px 10px',
          backgroundColor: COLORS.white,
          margin,
          borderBottom: isBorder ? '1px solid rgba(0, 0, 0, 0.12)' : undefined,
          borderRadius: rounded ? '12px' : undefined,
        }}
      >
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Box sx={{ width: '24px', height: '24px', padding: '4px' }}>
            <LocationOnIcon sx={{ color: COLORS.black06, width: '16px', height: '16px' }} />
          </Box>
          <Box sx={{ alignItems: 'flex-start', justifyContent: 'center', display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ wordBreak: 'break-word', textAlign: 'left', color: COLORS.black08, fontSize: '10px' }}>
              {location.type === 'FULL' ? addressString : null}
            </Typography>
            {(location.locationVisibility === 'SHOW_ADDRESS_LINK' || location.type !== 'FULL') && (
              <Link
                href={getLocationLink(location.type)}
                sx={{ fontSize: '9px', color: COLORS.purple }}
                target="_blank"
                variant="body2"
                rel="noopener noreferrer"
              >
                {t('template5:showMap')}
              </Link>
            )}
          </Box>
        </Box>
        {location.type === 'FULL' && location.locationVisibility === 'SHOW_MAP' && (
          <LoadScriptNext googleMapsApiKey={GOOGLE_MAP_KEY}>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={coordinates}
              zoom={15}
              options={{
                disableDefaultUI: true,
                draggable: false,
                zoomControl: false,
                scrollwheel: false,
                disableDoubleClickZoom: true,
                mapTypeControl: false,
                streetViewControl: false,
              }}
            >
              <Marker position={coordinates} />
            </GoogleMap>
          </LoadScriptNext>
        )}
      </Box>
    );
  },
);

VCardPreviewLocation.displayName = 'VCardPreviewLocation';
export default VCardPreviewLocation;
